import React, { useState } from "react";
import { Select, Button, Space } from "antd";
import { $t } from "~/i18n.js";
import { EyeInvisibleFilled, PlusOutlined, SplitCellsOutlined } from "@ant-design/icons";

// Flight state keeps 'DELETE' and -99 (privacy) sentinels in payer fields, only positive ids are real members
const isValidPayerId = (id) => typeof id === "number" && id > 0;

/**
 * PayingMemberSelector component for selecting the paying member(s) (betalend lid).
 * With split payment the cost is shared 50/50 between value (primary) and value2 (secondary).
 *
 * Props:
 * - value: current primary payer (user id)
 * - value2: current secondary payer (user id), only when splitting
 * - onChange: called with a patch { betalend_lid_id, betalend_lid_2_id }, the second one is 'DELETE' if not splitting
 * - vliegers: array of user objects { id, name }
 * - flight: flight object
 * - disabled: (optional) disables the select
 */
export function PayingMemberSelector({
  value,
  value2,
  onChange,
  vliegers,
  flight,
  disabled,
  onlyToggle,
}) {
  const [addingSecond, setAddingSecond] = useState(false);

  const gezagvoerderId =
    flight && flight.gezagvoerder_id > 0 && flight.gezagvoerder_id;
  const tweedeInzittendeId =
    flight && flight.tweede_inzittende_id > 0 && flight.tweede_inzittende_id;
  const isAfgeschermd = flight && flight.betalend_lid_id === -99;
  const buttonsDisabled = disabled || isAfgeschermd;
  const isDeleted =
    flight &&
    (flight.betalend_lid_id === "DELETE" || flight.betalend_lid_id < 0);

  const secondId = isValidPayerId(value2) ? value2 : null;
  const isSplit = !!secondId;

  const canSplit = !!gezagvoerderId && !!tweedeInzittendeId;

  const selectPrimary = (id) =>
    onChange({
      betalend_lid_id: id,
      betalend_lid_2_id: secondId && secondId !== id ? secondId : "DELETE",
    });

  const applyShortcut = (patch) => {
    setAddingSecond(false);
    onChange(patch);
  };

  const selectSecond = (id) => {
    onChange({
      betalend_lid_id: value,
      betalend_lid_2_id: id && id !== value ? id : "DELETE",
    });
    if (!id) setAddingSecond(false);
  };

  const filterOption = (input, option) => {
    // AntD v4: option.children, v5: option.label
    const label = option?.label || option?.children;
    if (typeof label === "string") {
      return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    }
    return false;
  };

  const renderOptions = (excludeId) =>
    vliegers &&
    vliegers.map(
      (user, key) =>
        user.id > 0 &&
        user.id !== excludeId && (
          <Select.Option key={key} value={user.id} label={user.name}>
            {user.name}
          </Select.Option>
        ),
    );

  return (
    <div>
      <Space style={{ marginBottom: 8 }}>
        <Button
          type={!isSplit && value === gezagvoerderId ? "primary" : "default"}
          onClick={() =>
            gezagvoerderId &&
            applyShortcut({
              betalend_lid_id: gezagvoerderId,
              betalend_lid_2_id: "DELETE",
            })
          }
          disabled={buttonsDisabled || !gezagvoerderId}
          size="small"
        >
          {$t("Gezagvoerder")}
        </Button>
        <Button
          type={
            !isSplit && value === tweedeInzittendeId ? "primary" : "default"
          }
          onClick={() =>
            tweedeInzittendeId &&
            applyShortcut({
              betalend_lid_id: tweedeInzittendeId,
              betalend_lid_2_id: "DELETE",
            })
          }
          disabled={buttonsDisabled || !tweedeInzittendeId}
          size="small"
        >
          {$t("Tweede inzittende")}
        </Button>
        <Button
          type={isSplit ? "primary" : "default"}
          onClick={() =>
            applyShortcut({
              betalend_lid_id: gezagvoerderId,
              betalend_lid_2_id: tweedeInzittendeId,
            })
          }
          disabled={buttonsDisabled || !canSplit}
          size="small"
          icon={<SplitCellsOutlined/>}
        >
          {$t("Splitsen")}
        </Button>
      </Space>

      {onlyToggle ? null : isAfgeschermd ? (
        <p>
          <EyeInvisibleFilled /> {$t("Afgeschermd")}
        </p>
      ) : (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space style={{ width: "100%" }}>
            <Select
              value={isDeleted ? undefined : value}
              onChange={selectPrimary}
              disabled={disabled}
              showSearch
              style={{ width: "100%", minWidth: 200 }}
              filterOption={filterOption}
              placeholder={$t("Selecteer betalend lid")}
            >
              {renderOptions(secondId)}
            </Select>
            {!isSplit && !addingSecond && isValidPayerId(value) && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                disabled={disabled}
                onClick={() => setAddingSecond(true)}
                title={$t("Tweede betalend lid toevoegen (50/50)")}
              />
            )}
          </Space>

          {(isSplit || addingSecond) && (
            <Select
              value={secondId || undefined}
              onChange={selectSecond}
              disabled={disabled}
              allowClear
              showSearch
              style={{ width: "100%", minWidth: 200 }}
              filterOption={filterOption}
              placeholder={$t("Selecteer tweede betalend lid (50%)")}
            >
              {renderOptions(value)}
            </Select>
          )}
        </Space>
      )}
    </div>
  );
}
